import { pricePerPound } from '../../price-units';
import {purchaseOrderTraders} from '../shared/purchase-order-traders';
import { handlingBreakdown } from '../shared/purchase-order-costs';
import { PurchaseOrderLookupsService } from '../shared/purchase-order-lookups.service';
import { Component, Output, EventEmitter, OnInit, OnDestroy, Input } from '@angular/core';
import { RmsModule } from '@rms';
import { Column, TableIcon } from '@interfaces';
import { WebService, lastValueFrom, RmsMessageService } from '@services';

import { CalcFormComponent } from '../calc-form/calc-form.component';

@Component({
  selector: 'app-multi-purchase-order-form',
  imports: [RmsModule, CalcFormComponent],
  templateUrl: './multi-purchase-order-form.component.html',
  styleUrls: ['./multi-purchase-order-form.component.scss']
})
export class MultiPurchaseOrderFormComponent implements OnInit, OnDestroy {
  @Input() selectedPurchaseOrders: any[] = [];
  @Output() CancelForm = new EventEmitter();
  @Output() onSavePOSpread = new EventEmitter();

  ngOnDestroy() { clearTimeout(this.formUpdateDebounce); clearTimeout(this.containerChangeTimeout); clearTimeout(this.bagsPerContainerChangeTimeout); }

  // ---- Debouncers / state ----
  private formUpdateDebounce: any;
  private containerChangeTimeout: any;
  private bagsPerContainerChangeTimeout: any;

  EditForm = true;
  loading = true;
  loadError = '';
  saving = false;
  saveOutcomeUnknown = false;
  building = false;
  allocationOpen = false;
  allocationVisited = false;

  goToDetails() {
    if (this.saving || this.building) return;
    this.EditForm = true;
    this.allocationOpen = false;
  }

  goToAllocation(detailsValid: boolean) {
    if (this.saving || this.building || !detailsValid || !this.TotalContainers || !this.BagsPerContainer) return;
    this.EditForm = true;
    this.allocationOpen = true;
    this.allocationVisited = true;
  }

  PurchaseOrders: any[] = [];
  /** Working list (parents with optional nested ChildInventory) built from warehouse spreads */
  Inventory: any[] = [];
  RemovedInventory: any[] = [];
  Warehouses: any[] = [];
  /** Optional: decaf-only products for child selector */
  DecafProducts: any[] = [];

  TotalContainers: number | null = null;
  BagsPerContainer: number | null = null;

  filteredPricings: any[] = []; // Filtered price descriptors based on Fair Trade
  MultiOrder: any = {};          // Shared across all POs/Inventory
  InventoryData: any = {};

  // ---------- Expandable preview ----------
  /** Expandable (parent with ChildInventory) projection for the table */
  InventoryForTable: any[] = [];
  autoExpand: boolean = false;

  TableExpand: boolean = false; // kept from your original, not required anymore

  // pricing display helpers
  currentPriceBase: number | null = null;
  PerPoundCost = '$0.0000';      // shared label for UI; per-warehouse displays own PerPoundCost

  // UI helpers
  closingTotals = false;
  SelectedInventory: any | null = null;

  // month pickers
  monthYearOptions: { label: string; value: Date }[] = [];
  filteredEndMonthOptions: { label: string; value: Date }[] = [];

  // tables
  InventoryColumns: Column[] = [];
  PurchaseOrderColumns: Column[] = [];
  ChildInventoryColumns: Column[] = [
    { label: 'Ref #', key: 'InventoryID', width: 8 },
    { label: 'Product', key: 'InventoryTypeName', width: 10 },
    { label: 'Bags', key: 'BagsOrdered', width: 10 },
    { label: 'Net Wt', key: 'NetWeight', width: 10, type: 'number', fixed: 4 },
    { labelKey: 'PriceHeader', key: 'PriceLabel', width: 15 },
    { labelKey: 'ShippingHeader', key: 'ShippingLabel', width: 12, type: 'currency', format: '1.4-4' },
    { labelKey: 'LandedHeader', key: 'LandedLabel', width: 12, type: 'currency', format: '1.4-4' },
  ];
  InventoryIcons: TableIcon[] = [];

  // constants / handling rules (match single-inventory)
  readonly DEFAULT_EX_WHSE = 0.00;
  readonly DEFAULT_EX_WHSE_FOR_NON_FOBEX = 0.02;

  // —— Decaf mapping (warehouse -> decaffeinator vendor)
  private readonly DECAF_WHSE_TO_VENDOR: Record<number, number> = {
    46: 6815, // example: Seaforth
    23: 1992, // example: DMT
  };

  DaysToFinanceList = [
    { name: '30 Days', value: 30 },
    { name: '60 Days', value: 60 },
    { name: '90 Days', value: 90 },
  ];

  constructor(private lookups: PurchaseOrderLookupsService, private web: WebService, private message: RmsMessageService) { }

  // ===========================================================================
  // Init / Data bootstrap
  // ===========================================================================
  async ngOnInit() {
    this.loading = true;
    this.loadError = '';
    try {
    // 1) Always have reference data ready (and decaf list) before anything else
    if (!this.InventoryData?.Products) {
      this.InventoryData = await this.GetInventoryData();
    }
    this.DecafProducts = (this.InventoryData?.Products ?? []).filter((p: any) => p.Decaf === true);

    // 2) Existing spread? Hydrate + build preview
    if (this.selectedPurchaseOrders?.length) {
      await this.hydrateFromExistingPOs(this.selectedPurchaseOrders);
      // Build the preview rows and auto-expand if any child exists
      this.buildPreviewRows();
      return;
    }

    // 3) New spread bootstrap
    this.generateMonthYearOptions();
    this.MultiOrder = { PurchaseDate: new Date(), ExWhseCost: this.DEFAULT_EX_WHSE };
    // Sort products for UI
    this.InventoryData.Products = (this.InventoryData.Products ?? [])
      .sort((a: any, b: any) => a.InventoryTypeName.localeCompare(b.InventoryTypeName));
    } catch (error) { this.loadError = 'Unable to load spread options. Please retry.'; }
    finally { this.loading = false; }
  }


  private async GetInventoryData() {
    const products$ = this.lookups.get('inventory', 'coffee/products');
    const paymentTerms$ = this.lookups.get('data', 'coffee/payment-terms');
    const sampleApprovalTerms$ = this.lookups.get('data', 'coffee/sample-approval-terms');
    const weightTypes$ = this.lookups.get('data', 'coffee/weight-types');
    const shipmentTerms$ = this.lookups.get('data', 'coffee/shipment-terms');
    const shipmentTypes$ = this.lookups.get('data', 'coffee/shipment-types');
    const futuresMonths$ = this.lookups.get('inventory', 'coffee/risk-board/futures');
    const destinationWarehouses$ = this.lookups.get('data', 'coffee/locations');
    const destinationPorts$ = this.lookups.get('data', 'coffee/destinations');
    const priceDescriptors$ = this.lookups.get('data', 'coffee/price-descriptors');
    const originRates$ = this.lookups.get('data', 'coffee/origin-rates');
    const costings$ = this.lookups.get('data', 'coffee/costings');
    const decaffeinators$ = this.lookups.get('data', 'coffee/decaffeinators');
    const traders$ = this.lookups.get('users', 'users');
    const suppliers$ = this.lookups.get('inventory', 'coffee/suppliers');

    const [
      Products,
      PaymentTerms,
      SampleApprovalTerms,
      WeightTypes,
      ShipmentTerms,
      ShipmentTypes,
      FuturesMonths,
      DestinationWarehouses,
      DestinationPorts,
      PriceDescriptors,
      OriginRates,
      Costings,
      Decaffeinators,
      Traders,
      Suppliers
    ] = await Promise.all([
      lastValueFrom(products$),
      lastValueFrom(paymentTerms$),
      lastValueFrom(sampleApprovalTerms$),
      lastValueFrom(weightTypes$),
      lastValueFrom(shipmentTerms$),
      lastValueFrom(shipmentTypes$),
      lastValueFrom(futuresMonths$),
      lastValueFrom(destinationWarehouses$),
      lastValueFrom(destinationPorts$),
      lastValueFrom(priceDescriptors$),
      lastValueFrom(originRates$),
      lastValueFrom(costings$),
      lastValueFrom(decaffeinators$),
      lastValueFrom(traders$),
      lastValueFrom(suppliers$),
    ]);

    return {
      AllFuturesMonths: FuturesMonths,
      Products,
      PaymentTerms,
      SampleApprovalTerms,
      WeightTypes,
      ShipmentTerms,
      ShipmentTypes,
      FuturesMonths,
      DestinationWarehouses,
      DestinationPorts,
      PriceDescriptors,
      OriginRates,
      Costings,
      Decaffeinators,
      Traders: purchaseOrderTraders(Traders),
      Suppliers,
      Months: [
        { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
        { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
        { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
        { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
      ],
      Years: this.getYears(),
      TollingContracts: []
    };
  }

  private getYears() {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 11 }, (_, i) => {
      const year = currentYear + i;
      return { label: year.toString(), value: year };
    });
  }

  // ===========================================================================
  // Date / Spread helpers
  // ===========================================================================
  generateMonthYearOptions() {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(start.getFullYear(), start.getMonth() + 24, 1);

    const options: { label: string; value: Date }[] = [];
    const current = new Date(start);

    while (current <= end) {
      const month = current.toLocaleString('en-US', { month: 'short' });
      const year = `'${current.getFullYear().toString().slice(-2)}`;
      options.push({ label: `${month} ${year}`, value: new Date(current) });
      current.setMonth(current.getMonth() + 1);
    }

    this.monthYearOptions = options;
    this.filteredEndMonthOptions = options;
  }

  private getMonthYearSpread(start: Date, end: Date): Date[] {
    const result = [];
    const current = new Date(start.getFullYear(), start.getMonth(), 1);
    while (current <= end) {
      result.push(new Date(current));
      current.setMonth(current.getMonth() + 1);
    }
    return result;
  }

  onStartDateChange(date: Date) {
    this.MultiOrder.StartShip = date;
    this.filterEndMonthOptions();
    this.onDateChange();
  }
  onEndDateChange(date: Date) {
    this.MultiOrder.EndShip = date;
    this.onDateChange();
  }
  filterEndMonthOptions() {
    if (!this.MultiOrder.StartShip) {
      this.filteredEndMonthOptions = this.monthYearOptions;
      return;
    }
    const startTime = this.MultiOrder.StartShip.getTime();
    this.filteredEndMonthOptions = this.monthYearOptions.filter(opt => opt.value.getTime() >= startTime);
    if (this.MultiOrder.EndShip && this.MultiOrder.EndShip.getTime() < startTime) {
      this.MultiOrder.EndShip = null;
    }
  }
  onDateChange() {
    this.onAnyFieldChanged();
    this.upsertPriceBaseByFuture();
    if (this.MultiOrder.StartShip && this.MultiOrder.EndShip) {
      this.upsertPriceBaseSpread(); // keep month spread entries in sync
      if (this.Warehouses.length === 0) this.addWarehouse();
      else this.updateWarehouse();
    }
  }

  // ===========================================================================
  // Handling / pricing events (match single-inventory rules)
  // ===========================================================================
  onShipmentTypeChange(newType: number) {
    this.MultiOrder.ShipmentTypeID = newType;

    if (this.MultiOrder.ShipmentTypeID !== 1 && this.MultiOrder.ShippingTerm !== 4) {
      if (this.MultiOrder.ExWhseCost == null) this.MultiOrder.ExWhseCost = this.DEFAULT_EX_WHSE_FOR_NON_FOBEX;
    }
    if (this.MultiOrder.ShippingTerm === 4) {
      this.MultiOrder.ExWhseCost = this.DEFAULT_EX_WHSE;
    }
    this.onAnyFieldChanged();
  }

  onShippingTermChange(newTerm: number) {
    this.MultiOrder.ShippingTerm = newTerm;

    if (newTerm === 4) {
      this.MultiOrder.ExWhseCost = this.DEFAULT_EX_WHSE;
    } else {
      if (this.MultiOrder.ShipmentTypeID !== 1 && this.MultiOrder.ExWhseCost == null) {
        this.MultiOrder.ExWhseCost = this.DEFAULT_EX_WHSE_FOR_NON_FOBEX;
      }
    }
    this.onAnyFieldChanged();
  }

  // ===========================================================================
  // Per-pound conversion for price/diff (shared driver)
  // ===========================================================================
  updatePerPoundCost(Warehouse?: any): void {
    if (Warehouse) {
      this.UpdateEachPerPoundCost(Warehouse);
    } else {
      this.Warehouses.forEach((w: any) => {
        if (w.DifferentialPerUnit != null || w.PricePerUnit != null) this.UpdateEachPerPoundCost(w);
      });
    }
  }

  private UpdateEachPerPoundCost(Warehouse: any) {
    setTimeout(() => {
      const differential = Number(this.MultiOrder.BuyingTermID) === 2;
      const value = pricePerPound(differential ? Warehouse.DifferentialPerUnit : Warehouse.PricePerUnit, this.MultiOrder.PriceDescriptorID);
      Warehouse.DifferentialPerPound = differential ? value : null;
      Warehouse.PricePound = differential ? null : value;

      Warehouse.PerPoundCostValue = value;
      Warehouse.PerPoundCost = new Intl.NumberFormat('en-US', {
        style: 'currency', currency: 'USD', minimumFractionDigits: 4, maximumFractionDigits: 4
      }).format(value || 0);
    });
  }

  // ===========================================================================
  // Warehouses & Spread rows
  // ===========================================================================
  addWarehouse() {
    const spread: any[] = [];
    const start = new Date(this.MultiOrder.StartShip);
    const end = new Date(this.MultiOrder.EndShip);
    const current = new Date(start);

    while (current <= end) {
      const month = current.getMonth() + 1;
      const year = current.getFullYear();
      const label = `${current.toLocaleString('en-US', { month: 'short' })} '${year.toString().slice(-2)}`;
      spread.push({ label, ShipMonth: month, ShipYear: year, Containers: null });
      current.setMonth(current.getMonth() + 1);
    }

    const newWarehouse = {
      id: crypto.randomUUID?.() ?? Math.random().toString(),
      FinalDestinationWarehouseID: null,
      Destination: null,
      Spread: spread,
      PricePerUnit: null,
      DifferentialPerUnit: null,
      PerPoundCost: '$0.0000',
      PerPoundCostValue: 0,

      // decaf flags/child are added via onChangeWarehouse()
    };

    this.Warehouses = [...this.Warehouses, newWarehouse];
  }

  updateWarehouse() {
    const spread: any[] = [];
    const current = new Date(this.MultiOrder.StartShip);
    while (current <= this.MultiOrder.EndShip) {
      const month = current.getMonth() + 1;
      const year = current.getFullYear();
      const label = `${current.toLocaleString('en-US', { month: 'short' })} '${year.toString().slice(-2)}`;
      spread.push({ label, ShipMonth: month, ShipYear: year, Containers: null });
      current.setMonth(current.getMonth() + 1);
    }
    this.Warehouses.forEach((w: any) => (w.Spread = spread));
  }

  onRemoveWarehouse(index: number) {
    this.Warehouses.splice(index, 1);
  }

  filteredDestinationWarehousesFor(index: number): any[] {
    const rows = this.Warehouses ?? [];
    const me = rows[index];
    const currentID = me?.Decaf ? null : (me?.FinalDestinationWarehouseID ?? null);

    // Only consider non-decaf rows (parents) other than me, and only truthy IDs
    const used = new Set<number>(
      rows
        .filter((w, i) => i !== index && !w?.Decaf && w?.FinalDestinationWarehouseID != null)
        .map(w => Number(w.FinalDestinationWarehouseID))
    );

    return (this.InventoryData?.DestinationWarehouses ?? []).filter((dw: any) =>
      // keep the current selection even if it appears "used"
      dw?.LocationID === currentID || !used.has(Number(dw?.LocationID))
    );
  }

  filteredDecafDestinationWarehousesFor(index: number): any[] {
    const rows = this.Warehouses ?? [];
    const me = rows[index];
    const allOptions = this.InventoryData?.DestinationWarehouses ?? [];

    // If no child yet, show all
    if (!me?.Child) return allOptions;

    const currentID = me.Child?.FinalDestinationWarehouseID ?? null;

    // Distinct parent decaf destinations (use only rows with Decaf === true)
    const decafParentIds = Array.from(
      new Set(
        rows
          .filter(w => !!w?.Decaf && w?.FinalDestinationWarehouseID != null)
          .map(w => Number(w.FinalDestinationWarehouseID))
      )
    );

    const multipleDistinctDecafParents = decafParentIds.length >= 2;

    // If parents are going to different decaf locations, allow any child destination (no filtering)
    if (multipleDistinctDecafParents) {
      return allOptions;
    }

    // Otherwise (parents share the same decaf location), enforce unique child FinalDestinationWarehouse
    const usedChildIds = new Set<number>(
      rows
        .filter((w, i) => i !== index && w?.Child?.FinalDestinationWarehouseID != null)
        .map(w => Number(w.Child.FinalDestinationWarehouseID))
    );

    return allOptions.filter((dw: any) =>
      // always keep the currently selected option
      Number(dw?.LocationID) === Number(currentID) || !usedChildIds.has(Number(dw?.LocationID))
    );
  }

  // ===========================================================================
  // Form field change orchestration
  // ===========================================================================
  onBuyingTermChange() {
    if (this.Warehouses) {
      this.Warehouses.forEach((inv: any) => {
        if (this.MultiOrder.BuyingTermID === 2) {
          inv.PricePound = null;
          inv.PricePerUnit = null;
        }
        else {
          inv.DifferentialPerPound = null;
          inv.DifferentialPerUnit = null;
        }
      });
    }
    this.onAnyFieldChanged();
  }
  onAnyFieldChanged() {
    clearTimeout(this.formUpdateDebounce);
    this.formUpdateDebounce = setTimeout(() => {
      this.updatePurchaseOrdersFromForm();
      this.updatePerPoundCost();
    }, 250);
  }

  onContainerChange(value: number) {
    clearTimeout(this.containerChangeTimeout);
    this.containerChangeTimeout = setTimeout(() => (this.TotalContainers = value), 500);
  }
  onBagsPerContainerChange(value: number) {
    clearTimeout(this.bagsPerContainerChangeTimeout);
    this.bagsPerContainerChangeTimeout = setTimeout(() => (this.BagsPerContainer = value), 500);
  }

  SetPriceToBeFixedBy() {
    this.MultiOrder.PriceToBeFixedByRoyal = this.MultiOrder.PriceFixedBy == 1;
    this.MultiOrder.PriceToBeFixedByShipper = this.MultiOrder.PriceFixedBy == 2;
  }

  // ===========================================================================
  // Build / preview spread
  // ===========================================================================
  async updatePurchaseOrdersFromForm() {
    if (!this.MultiOrder?.StartShip || !this.MultiOrder?.EndShip) return;

    // Price basis
    if (this.MultiOrder.InventoryTypeID && !this.currentPriceBase) {
      const prod = this.InventoryData.Products.find((p: any) => p.InventoryTypeID == this.MultiOrder.InventoryTypeID);
      if (prod?.FuturesExchangeKey) {
        const basis = await lastValueFrom(
          this.web.getWebCall('data', 'coffee/price-basis', { params: [{ name: 'ExchangeKey', value: prod.FuturesExchangeKey }] })
        );
        this.currentPriceBase = basis?.length ? basis[0].PriceBase : 2.75;
        this.backfillPriceBaseDefaults();
      }
    }

    // Futures filter by exchange + filtered pricings
    if (this.MultiOrder.InventoryTypeID) {
      const prod = this.InventoryData.Products.find((p: any) => p.InventoryTypeID == this.MultiOrder.InventoryTypeID);
      this.MultiOrder.Country = this.MultiOrder.Country ?? prod?.Country;
      this.MultiOrder.WeightTypeID = this.MultiOrder.WeightTypeID ?? prod?.WeightPurchaseSku;
      this.MultiOrder.NetWeight = this.MultiOrder.NetWeight ?? prod?.WeightDefaultValue;
      this.MultiOrder.FairTrade = this.MultiOrder.FairTrade ?? prod?.FairTrade;

      this.filteredPricings = (prod?.FairTrade || this.MultiOrder.FairTrade)
        ? this.InventoryData.PriceDescriptors.filter((pd: any) => pd.PriceDescriptorID != 2)
        : this.InventoryData.PriceDescriptors;

      if (prod?.FuturesExchangeKey) {
        this.InventoryData.FuturesMonths =
          (this.InventoryData.AllFuturesMonths || []).filter((fm: any) => fm.Exchange === prod.FuturesExchangeKey);
        this.upsertPriceBaseByFuture();
        this.backfillPriceBaseDefaultsByFuture();
      }
    }
  }
  DecafMonthYear(month: number, year: number): { month: number; year: number } {
    const m = Number(month), y = Number(year);
    if (!Number.isFinite(m) || !Number.isFinite(y)) throw new Error('Invalid month/year');

    // Accept 1–12 (domain style) or 0–11 (Date style)
    const zeroBased = (m >= 1 && m <= 12) ? m - 1 : m;

    const d = new Date(y, zeroBased, 1);
    d.setMonth(d.getMonth() + 1); // add 2 months

    return { month: d.getMonth(), year: d.getFullYear() }; // return 1–12 month
  }
  async onBuildPurchaseOrders() {
    if (this.building || this.saving) return;
    this.building = true;
    try {
    this.PurchaseOrders = [];
    this.Inventory = [];

    if (this.MultiOrder.FairTrade) {
      const ftMin = this.MultiOrder.FairTradeMinimum;
      const ftPrem = this.MultiOrder.FairTradePremium;
      const orgPrem = this.MultiOrder.OrganicPremium;
      const ctryPrem = this.MultiOrder.CountryPremium;

      const missingFT =
        ftMin == null || ftMin === '' || Number(ftMin) === 0 ||
        ftPrem == null || ftPrem === '' || Number(ftPrem) === 0 ||
        orgPrem == null || orgPrem === '' || Number(orgPrem) === 0 ||
        ctryPrem == null || ctryPrem === '' || Number(ctryPrem) === 0;

      if (missingFT) {
        const proceed = confirm(
          'One or more Fair Trade costs (Minimum, Premium, Organic, Country) are not filled in or are zero.\n\nDo you want to continue anyway?'
        );
        if (!proceed) return; // abort
      }
    }

    let j = 0;
    for (const w of this.Warehouses) {
      for (const s of w.Spread.filter((x: any) => Number(x.Containers) > 0)) {
        for (let i = 0; i < Number(s.Containers); i++) {
          // ---------- Build PARENT ----------
          const parent: any = {
            _tempKey: crypto.randomUUID?.() ?? Math.random().toString(36).slice(2),
            InventoryTypeID: this.MultiOrder.InventoryTypeID,
            FairTrade: this.MultiOrder.FairTrade,
            FairTradeMinimum: this.MultiOrder.FairTradeMinimum,
            FairTradePremium: this.MultiOrder.FairTradePremium,
            OrganicPremium: this.MultiOrder.OrganicPremium,
            CountryPremium: this.MultiOrder.CountryPremium,
            NetWeight: this.MultiOrder.NetWeight,
            Marks: this.MultiOrder.Marks,
            WeightTypeID: this.MultiOrder.WeightTypeID,
            BagsOrdered: this.BagsPerContainer!,
            ETDMonth: s.ShipMonth,
            ETDYear: s.ShipYear,
            ShippingTerm: this.MultiOrder.ShippingTerm,
            Destination: this.InventoryData.DestinationWarehouses.find(
              (dw: any) => dw.LocationID == w.FinalDestinationWarehouseID
            )?.DestinationId,
            FinalDestinationWarehouseID: w.FinalDestinationWarehouseID,
            PaymentTermsID: this.MultiOrder.PaymentTermsID,
            ShipmentTypeID: this.MultiOrder.ShipmentTypeID,
            SASTermID: this.MultiOrder.SASTermID,
            PriceDescriptorID: this.MultiOrder.PriceDescriptorID,

            // Price / Diff (normalized downstream)
            PricePerUnit: this.MultiOrder.BuyingTermID === 2 ? null : w.PricePerUnit,
            DifferentialPerUnit: this.MultiOrder.BuyingTermID === 2 ? w.DifferentialPerUnit : null,

            SupplierCommission: this.MultiOrder.SupplierCommission,
            RainforestCommission: this.MultiOrder.RainforestCommission,
            OrganicCommission: this.MultiOrder.OrganicCommission,
            IntermediaryCommission: this.MultiOrder.IntermediaryCommission,
            CommissionOtherName: this.MultiOrder.CommissionOtherName,
            CommissionOtherLevel: this.MultiOrder.CommissionOtherLevel,

            BuyingTermID: this.MultiOrder.BuyingTermID,
            PriceToBeFixedByRoyal: this.MultiOrder.PriceToBeFixedByRoyal,
            PriceToBeFixedByShipper: this.MultiOrder.PriceToBeFixedByShipper,
            Intermediary: this.MultiOrder.Intermediary,
            IntermediaryNumber: this.MultiOrder.IntermediaryNumber,

            // Per-pound values computed from the warehouse row
            PricePound: w.PricePound,
            DifferentialPerPound: w.DifferentialPerPound,

            MonthLabel: s.label,
            WarehouseLabel: this.InventoryData.DestinationWarehouses.find(
              (dw: any) => dw.LocationID == w.FinalDestinationWarehouseID
            )?.LocationName,
            Notes: null,
            WebInvisible: false,
            SupplierReffNumber: null,
            poNum: j,
            expandable: w.Child ? true : false,
            expanded: w.Child ? true : false,
          };

          const monthBase = this.getPriceBaseForMonth(s.ShipYear, s.ShipMonth);
          parent.FixationCurrentLevel = monthBase;

          // Handling defaults at build time
          if (parent.ShippingTerm === 4) {
            parent.ExWhseCost = parent.ExWhseCost ?? this.DEFAULT_EX_WHSE;
          } else if (parent.ShipmentTypeID !== 1) {
            parent.ExWhseCost = (
              this.InventoryData.DestinationWarehouses.find((dw: any) => dw.LocationID === w.FinalDestinationWarehouseID)?.LocationExWarehouseCost
              ?? this.DEFAULT_EX_WHSE_FOR_NON_FOBEX
            );
          }

          const priceDescriptor = this.InventoryData.PriceDescriptors.find((pd: any) => pd.PriceDescriptorID === this.MultiOrder.PriceDescriptorID)?.PriceDescriptorName;

          parent.FinalPosition = this.getFinalPosition(s);
          parent.FutureCompositeID = await this.getClosestFuturesMonth(parent);
          parent.FixationCurrentLevel =
            this.getPriceBaseForComposite(parent.FutureCompositeID) ??
            this.currentPriceBase ?? null;

          await this.recalculateInventoryLine(parent, s, w);

          if (this.MultiOrder.BuyingTermID === 2) {
            parent.CostPerUnitLabel =
              this.InventoryData.FuturesMonths.find((fm: any) => fm.FutureCompositeID == parent.FutureCompositeID)?.FutureCompositeName +
              (w.DifferentialPerUnit >= 0 ? '+' : '') + (w.DifferentialPerUnit ?? 0).toFixed(4) + ' ' + (priceDescriptor ?? '');
          } else {
            parent.CostPerUnitLabel = (w.PricePerUnit ?? 0).toFixed(4) + ' ' + (priceDescriptor ?? '');
          }

          // prefill extras by spread key (existing-PO hydrate)
          const key = this.spreadKey(w.FinalDestinationWarehouseID, s.ShipYear, s.ShipMonth);
          const metaArr = this.prefillBySpreadKey.get(key);
          if (metaArr?.length) {
            const meta = metaArr.shift()!;
            parent.SupplierReffNumber = meta.SupplierReffNumber;
            parent.Notes = meta.Notes;
            parent.WebInvisible = meta.WebInvisible;
            parent.ExWhseCost = meta.ExWhseCost ?? parent.ExWhseCost;
            parent.InventoryID = meta.InventoryID

            if (parent.ShipmentTypeID === 1 && parent.ShippingTerm !== 4) {
              parent.FOBtoExWarehouseValueTemp = meta.FOBtoExWarehouseValueTemp ?? parent.FOBtoExWarehouseValueTemp;
            } else {
              parent.ExWhseCost = meta.ExWhseCost ?? parent.ExWhseCost;
            }
          }
          // ---------- Optional DECAF CHILD ----------
          let child: any | null = null;
          if (w.Decaf && w.Child?.DecaffeinatorID && w.Child?.InventoryTypeID && w.Child?.FinalDestinationWarehouseID) {
            // seed child from warehouse "Child" panel
            child = {
              ...parent,
              ...w.Child,
              IsChild: true,
              readOnly: true,
              ParentInventoryID: parent.InventoryID ?? null, // will be linked after save
              InventoryTypeID: w.Child.InventoryTypeID,
              InventoryID: w.Child?.InventoryID ?? undefined,
              FinalDestinationWarehouseID: w.Child.FinalDestinationWarehouseID,
              Destination: w.Child.Destination,
              DecaffeinatorID: w.Child.DecaffeinatorID,
              BagsOrdered: w.Child.BagsOrdered ?? parent.BagsOrdered,
              NetWeight: 132.28,
              _tempKey: null,
              ETDMonth: this.DecafMonthYear(s.ShipMonth, s.ShipYear).month,
              ETDYear: this.DecafMonthYear(s.ShipMonth, s.ShipYear).year,
              // carry labels/preview fields as needed:
              InventoryTypeName: this.InventoryData.Products.find((p: any) => p.InventoryTypeID == w.Child.InventoryTypeID)?.InventoryTypeName ?? null,
              WarehouseLabel: this.InventoryData.DestinationWarehouses.find(
                (dw: any) => dw.LocationID == w.Child.FinalDestinationWarehouseID
              )?.LocationName,
              expandable: true,
              expanded: true, 

            };
            if (this.MultiOrder.BuyingTermID === 2) {
              child.CostPerUnitLabel =
                this.InventoryData.FuturesMonths.find((fm: any) => fm.FutureCompositeID == child.FutureCompositeID)?.FutureCompositeName +
                (w.DifferentialPerUnit >= 0 ? '+' : '') + (w.DifferentialPerUnit ?? 0).toFixed(4) + ' ' + (priceDescriptor ?? '');
            } else {
              child.CostPerUnitLabel = (w.PricePerUnit ?? 0).toFixed(4) + ' ' + (priceDescriptor ?? '');
            }

            delete child._tempKey;
            // move handling/tolling from parent to child per rules
            this.enforceDecafHandlingForSpread(parent, child, w.TollingCost ?? this.MultiOrder.TollingCost);

            // if you want to run your decaf calc:
            child = await this.recalculateDecafInventoryLine(child, parent, w, s);
          }
          // ---------- Attach to PurchaseOrder & working arrays ----------
          const po = {
            PurchaseDate: this.MultiOrder.PurchaseDate,
            TraderGUID: this.MultiOrder.TraderGUID,
            SupplierID: this.MultiOrder.SupplierID,
            SupplierAgent: this.MultiOrder.SupplierAgent,
            Connected: false,
            TariffInPrice: false,
            Inventory: child ? [parent, child] : [parent],   // << include child in same PO
            poNum: j,
            SpreadKey: key
          };

          this.PurchaseOrders.push(po);

          // For building preview, keep just parent (with its nested ChildInventory)
          if (child) parent.ChildInventory = [child];
          this.Inventory.push(parent);

          if (child) this.TableExpand = true;
          j++;
        }
      }
    }

    // Columns & icons
    setTimeout(() => {
      this.PurchaseOrderColumns = [
        { label: 'Ship Date', key: 'MonthLabel' },
        { label: 'Destination', key: 'WarehouseLabel' },
        { label: 'Bags', key: 'BagsOrdered' },
        {
          label: 'Price/Diff Per Pound',
          key: 'CostPerUnitLabel',
          type: 'text'
        },
        {
          label: this.MultiOrder.ShipmentTypeID == 1 && this.MultiOrder.ShippingTerm != 4 ? 'FOBEXWhse-Temp' : 'Ex-Whse',
          key: this.MultiOrder.ShipmentTypeID == 1 && this.MultiOrder.ShippingTerm != 4 ? 'FOBtoExWarehouseValueTemp' : 'ExWhseCost',
          type: 'currency',
          edit: this.MultiOrder.ShipmentTypeID != 1 || this.MultiOrder.ShippingTerm === 4, // editable only when not FOBEX
          onAction: (row: any) => this.onExWhseCostEdited(row),
          format: '1.4-4', step: 0.0001
        },
        {
          label: 'Estimated Landed $/lb',
          key: this.MultiOrder.BuyingTermID == 2 ? 'EstimatedCost' : 'FinalWarehouseInCost',
          type: 'currency'
        },
        {
          label: 'Supplier Ref#', type: 'text', key: 'SupplierReffNumber',
          edit: true, onAction: (row: any) => { this.PurchaseOrders.find((po: any) => po.poNum == row.poNum).Inventory[0].SupplierReffNumber = row.SupplierReffNumber; }
        },
        {
          label: 'Notes', type: 'textarea', key: 'Notes',
          edit: true, onAction: (row: any) => { this.PurchaseOrders.find((po: any) => po.poNum == row.poNum).Inventory[0].Notes = row.Notes; }
        },
        {
          label: 'Web Invisible', type: 'checkbox', key: 'WebInvisible',
          edit: true, onAction: (row: any) => { this.PurchaseOrders.find((po: any) => po.poNum == row.poNum).Inventory[0].WebInvisible = row.WebInvisible; }
        },
      ];
      this.InventoryIcons = [
        { icon: 'info', action: (row) => this.openCostingWorksheet(row), title: 'View Calculations.' }
      ];

      // Build nested preview + auto expand
      this.buildPreviewRows();
      this.EditForm = false; // show preview
    });
      } catch (error: any) { this.message.error(error?.message ?? 'Unable to build the spread.'); }
    finally { this.building = false; }
  }

  // ---------- Decaf helpers ----------
  onChangeWarehouse(w: any, Warehouse: number) {
    const vendor = this.DECAF_WHSE_TO_VENDOR[Warehouse];
    if (!vendor) {
      // Clear decaf mode if switching away
      w.Decaf = false;
      delete w.Child;
      return;
    }

    // Enable decaf and seed child payload (keeping your original defaults)
    w.Decaf = true;
    w.Child = {
      DecaffeinatorID: vendor,
      InventoryTypeID: w.Child?.InventoryTypeID ?? null,
      FinalDestinationWarehouseID: w.Child?.FinalDestinationWarehouseID ?? null,
      Destination: w.Child?.Destination ?? null,
      // ----- your original decaf defaults -----
      DecafCostPerPound: 0.76,
      DecafCostTotal: 0,
      decafSeaforth: vendor === 6815 ? 800 : 0,
      decafStripping: 0,
      decafStrippingPerBag: 1.10,
      decafStorage: 0,
      decafStoragePerBag: 0.79,
      decafWeighing: 0,
      decafWeighingPerBag: 0.63,
      decafLossValue: 6.25,
      decafLossValuePercent: 6.25,
      totalDecafLossValue: 0,
      decafFinancing: 0,
      decafFinancingPercent: 10.00,
      totalDecafNetWeight: 0,
      totalDecafNetWeightFromLoss: 0,
      decafBuffer: 0.03,
      decafTolling: 0,
      decafTollingPerPound: 0,
      totalOrderWithDecafCost: 0,
      decafTrucking: 0,
      tollingCost: 0,
    };
    this.DecafProducts = this.InventoryData.Products.filter((p: any) => p.Decaf == true);
  }

  /** Move handling to child; zero parent, respecting FOBEX vs Ex-Whse */
  private enforceDecafHandlingForSpread(parent: any, child: any, tolling: number | null | undefined) {
    if (!child?.DecaffeinatorID) return;
    const toll = Number(tolling ?? 0);

    parent.FOBtoExWarehouseValueTemp = 0.00;
    parent.ExWhseCost = 0.00;
    parent.FinalWarehouseInCost = null;
    parent.FinalWarehouseInDifferential = null;

    if (parent.ShipmentTypeID === 1 && parent.ShippingTerm !== 4) {
      child.FOBtoExWarehouseValueTemp = toll;
      child.ExWhseCost = null;
    } else {
      child.ExWhseCost = toll;
      child.FOBtoExWarehouseValueTemp = null;
    }
  }

  // ===========================================================================
  // Pricing / costing
  // ===========================================================================
  onExWhseCostEdited(row: any) {
    const newVal = Number(row.ExWhseCost ?? 0);

    const po = this.PurchaseOrders.find((p: any) => p.poNum === row.poNum);
    if (!po) return;

    const inv = po.Inventory?.[0];
    if (!inv) return;

    inv.ExWhseCost = newVal;
    row.ExWhseCost = newVal;

    const ctx = this.findSpreadContext(inv);
    if (!ctx) { this.Inventory = [...this.Inventory]; return; }

    this.recalculateInventoryLine(inv, ctx.s, ctx.w).then(() => {
      this.Inventory = [...this.Inventory];
      this.buildPreviewRows();
    });
  }

  private findSpreadContext(inv: any): { w: any, s: any } | null {
    const w = this.Warehouses.find(
      (wh: any) => wh.FinalDestinationWarehouseID === inv.FinalDestinationWarehouseID
    );
    if (!w) return null;

    const s = Array.isArray(w.Spread)
      ? w.Spread.find((sp: any) => sp.ShipMonth === inv.ETDMonth && sp.ShipYear === inv.ETDYear)
      : null;

    return s ? { w, s } : null;
  }

  // ===========================================================================
  // Futures helpers
  // ===========================================================================
  getClosestFuturesMonth(Inventory: any) {
    const { ETDMonth, ETDYear } = Inventory;
    const futuresMonths = this.InventoryData?.FuturesMonths;
    if (!ETDMonth || !ETDYear || !futuresMonths?.length) return;

    const etd = new Date(Number(ETDYear), Number(ETDMonth) - 1, 1);
    const etdPlus45 = new Date(etd);
    etdPlus45.setDate(etdPlus45.getDate() + 45);

    let closest: any = null;
    let minDiff = Number.POSITIVE_INFINITY;
    for (const fm of futuresMonths) {
      const fndDate = new Date(fm.FND);
      if (isNaN(fndDate.getTime())) continue;
      const diff = Math.abs(fndDate.getTime() - etdPlus45.getTime());
      if (diff < minDiff) { minDiff = diff; closest = fm; }
    }
    return closest?.FutureCompositeID;
  }

  getFinalPosition(s: any) {
    const termName = this.InventoryData.ShipmentTerms
      .find((st: any) => st.ShipmentTermId == this.MultiOrder.ShippingTerm)?.ShipmentTermName?.toUpperCase() || '';
    return s.label.split(' ').join('') + ' ' + termName;
  }

  // ===========================================================================
  // Core per-line costing
  // ===========================================================================
  private async recalculateInventoryLine(inv: any, s: any, w: any): Promise<void> {
    const today = new Date();

    const destinationId = this.InventoryData.DestinationWarehouses.find(
      (fd: any) => fd.LocationID == inv.FinalDestinationWarehouseID
    )?.DestinationId;

    const selectedPort = this.InventoryData.DestinationPorts.find(
      (dp: any) => dp.destination_id == destinationId
    ) ?? {};

    const shipDate = new Date(s.ShipYear, s.ShipMonth - 1, 1);
    const availableRates = (this.InventoryData.OriginRates || []).filter(
      (or: any) => or.CountryID == this.MultiOrder.Country && or.DestinationID == inv.FinalDestinationWarehouseID
    );

    let selectedRate = availableRates
      .filter((or: any) => new Date(or.DateFrom) <= shipDate && new Date(or.DateGoodThru) >= shipDate)
      .sort((a: any, b: any) => new Date(b.DateFrom).getTime() - new Date(a.DateFrom).getTime())[0];

    if (!selectedRate) {
      selectedRate = availableRates
        .filter((or: any) => new Date(or.DateFrom) <= today)
        .sort((a: any, b: any) => b.SteamshipRate - a.SteamshipRate)[0];
      inv.WorstCase = true;
    }

    if (!selectedRate && inv.ShipmentTypeID === 1 && inv.ShippingTerm !== 4) throw new Error('No applicable freight rate was found for a spread destination. Check the rate setup.');
    const buffer = inv.WorstCase ? (selectedRate?.AddedBuffer ?? 0) : (selectedRate?.Buffer ?? 0);
    const bagsOrdered = inv.BagsOrdered;
    const netWeight = bagsOrdered * this.MultiOrder.NetWeight;
    const safeNetWeight = netWeight > 0 ? netWeight : 1;

    /// normalize price inputs to per pound
    if (this.MultiOrder.BuyingTermID === 2) {
      inv.DifferentialPerPound = w.DifferentialPerPound ?? 0;
      inv.PricePound = null;
    } else {
      inv.PricePound = w.PricePound ?? 0;
      inv.DifferentialPerPound = null;
    }

    const baseCost = (this.MultiOrder.BuyingTermID === 2
      ? (this.currentPriceBase ?? 0) + (inv.DifferentialPerPound ?? 0)
      : (inv.PricePound ?? 0)) + this.TotalCommissions(inv);

    const totalCost = baseCost * netWeight;

    // fixed fees
    inv.shipDate = shipDate;
    inv.TotalCommissions = this.TotalCommissions(inv) ?? 0;
    inv.cost = baseCost;
    inv.steamShipRate = Number(inv.CostOverrides?.steamShipRate ?? selectedRate?.SteamshipRate ?? 0);
    inv.dutyAndEntry = Number(selectedPort.duty_entry_fee ?? 0);
    inv.drayage = Number(selectedPort.drayage_cost ?? 0);
    inv.stripping = Number(selectedPort.container_unloading ?? 0);
    inv.storage = Number(selectedPort.storage_fee ?? 0);
    inv.weighing = Number(selectedPort.weight_verification ?? 0);
    inv.financing = Number(selectedPort.financial_charges ?? 0);
    inv.sampleCharge = Number(selectedPort.sample_inspection ?? 0);
    inv.rateBuffer = Number(inv.CostOverrides?.rateBuffer ?? buffer);
    inv.finalCostWith = inv.CostOverrides?.finalCostWith ?? s.finalCostWith ?? 0.015;
    inv.DaysToFinance = inv.CostOverrides?.DaysToFinance ?? s.DaysToFinance ?? 30;
    inv.fot = inv.CostOverrides?.fot ?? s.fot ?? 0;
    inv.rfa = inv.CostOverrides?.rfa ?? s.rfa ?? 0;
    inv.marineInsurance = inv.CostOverrides?.marineInsurance ?? s.marineInsurance ?? 0.17;

    inv.netWeight = netWeight;
    inv.totalCost = totalCost;

    Object.assign(inv, handlingBreakdown(inv));

    const baseEstimate = inv.ShipmentTypeID === 1
      ? (inv.totalEstimate + inv.rateBuffer + inv.finalCostWith)
      : (inv.ExWhseCost ?? this.DEFAULT_EX_WHSE_FOR_NON_FOBEX);

    inv.FOBtoExWarehouseValueTemp = inv.ShipmentTypeID === 1 ? parseFloat((baseEstimate).toFixed(4)) : null;

    // handling adder per rules
    const adder = this.getHandlingAdder(inv, baseEstimate);

    if (this.MultiOrder.BuyingTermID === 2) {
      inv.FinalWarehouseInCost = null;
      inv.FinalWarehouseInDifferential = parseFloat((adder + (inv.DifferentialPerPound ?? 0) + inv.TotalCommissions).toFixed(4));
      inv.EstimatedCost = parseFloat((adder + (inv.FixationCurrentLevel ?? 0) + (inv.DifferentialPerPound ?? 0) + inv.TotalCommissions).toFixed(4));
    } else {
      inv.FinalWarehouseInCost = parseFloat((adder + (inv.PricePound ?? 0) + inv.TotalCommissions).toFixed(4));
      inv.FinalWarehouseInDifferential = null;
      inv.EstimatedCost = null;
    }
  }

  private async recalculateDecafInventoryLine(child: any, parent: any, w: any, s: any) {
    // keep your logic, but using provided "child" instead of accidentally reading parent.inv.child
    const inv = { ...child };
    const rawBags = (parent.BagsOrdered * parent.NetWeight) / inv.NetWeight;
    const adjustedBags = Math.floor(rawBags * 0.95);
    inv.BagsOrdered = adjustedBags;

    const netWeight = inv.NetWeight * adjustedBags;
    const destinationId = this.InventoryData.DestinationWarehouses.find(
      (fd: any) => fd.LocationID == inv.FinalDestinationWarehouseID
    )?.DestinationId;

    const selectedPort = this.InventoryData.DestinationPorts.find(
      (dp: any) => dp.destination_id == destinationId
    ) ?? {};

    // Buy term normalization
    if (this.MultiOrder.BuyingTermID == 2) {
      inv.BuyingTermID = 2;
      inv.DifferentialPerUnit = w.DifferentialPerUnit;
      inv.DifferentialPerPound = w.PerPoundCostValue ?? w.perPoundCostValue ?? w.DifferentialPerPound ?? 0;
      inv.PricePerUnit = null;
      inv.PricePound = null;
    } else {
      inv.BuyingTermID = 1;
      inv.DifferentialPerUnit = null;
      inv.DifferentialPerPound = null;
      inv.PricePerUnit = w.PricePerUnit;
      inv.PricePound = w.PerPoundCostValue ?? w.perPoundCostValue ?? w.PricePound ?? 0;
    }
    const preLossNet = Number(parent.netWeight || 0); // parent pre-loss
    const baseFinal = Number(parent.FinalWarehouseInCost ?? parent.EstimatedCost ?? 0);

    // weight loss & post-loss
    inv.totalDecafNetWeightFromLoss = preLossNet * ((inv.decafLossValuePercent ?? 0) / 100);
    inv.totalDecafNetWeight = preLossNet - inv.totalDecafNetWeightFromLoss;

    // per-bag fees use **child** bags (you already compute adjustedBags)
    inv.decafStripping = (inv.decafStrippingPerBag ?? 0) * inv.BagsOrdered;
    inv.decafStorage = (inv.decafStoragePerBag ?? 0) * inv.BagsOrdered;
    inv.decafWeighing = (inv.decafWeighingPerBag ?? 0) * inv.BagsOrdered;

    // Seaforth
    inv.decafSeaforth = (inv.DecaffeinatorID == 6815) ? 800 : (inv.decafSeaforth ?? 0);

    // ✅ core decaf math
    inv.DecafCostTotal = (inv.DecafCostPerPound ?? 0.76) * preLossNet;
    inv.decafLossValue = (baseFinal * preLossNet) * ((inv.decafLossValuePercent ?? 0) / 100);
    inv.decafFinancing = ((baseFinal * preLossNet) * ((inv.decafFinancingPercent ?? 0) / 100)) / 365 * 120;

    const total = (inv.DecafCostTotal ?? 0) + (inv.decafSeaforth ?? 0) + (inv.decafStripping ?? 0) +
      (inv.decafStorage ?? 0) + (inv.decafWeighing ?? 0) +
      (inv.decafLossValue ?? 0) + (inv.decafFinancing ?? 0) + (inv.decafTrucking ?? 0);
    inv.decafTolling = total;
    inv.decafTollingPerPound = inv.decafTolling / preLossNet;
    inv.tollingCost = (inv.decafTollingPerPound ?? 0) + (this.TotalCommissions(inv) ?? 0);

    // final child results
    inv.FinalWarehouseInCost = (inv.PricePound ?? 0) + (inv.decafTollingPerPound ?? 0) + (this.TotalCommissions(inv) ?? 0);
    inv.FinalWarehouseInDifferential = (inv.DifferentialPerPound ?? 0) + (inv.decafTollingPerPound ?? 0) + (this.TotalCommissions(inv) ?? 0);
    inv.EstimatedCost = (inv.FinalWarehouseInDifferential ?? 0) + (parent.FixationCurrentLevel ?? 0);

    inv.FOBtoExWarehouseValueTemp = inv.tollingCost
    return inv;
  }

  private getHandlingAdder(inv: any, baseEstimate: number): number {
    const exWhseCost = inv.ExWhseCost ?? this.DEFAULT_EX_WHSE_FOR_NON_FOBEX;
    if (inv.ShippingTerm === 4) return exWhseCost; // forced none
    if (inv.ShipmentTypeID === 1) {
      return (inv.FOBtoExWarehouseValueTemp ?? baseEstimate ?? 0);
    }
    return (exWhseCost);
  }

  // ===========================================================================
  // Totals popup
  // ===========================================================================

  closeTotals() {
    this.closingTotals = true;
    setTimeout(() => {
      this.SelectedInventory = null;
      this.closingTotals = false;
    }, 300);
  }

  // ===========================================================================
  // Misc helpers
  // ===========================================================================
  TotalCommissions(inv: any) {
    return (
      (inv.SupplierCommission ?? 0) +
      (inv.RainforestCommission ?? 0) +
      (inv.OrganicCommission ?? 0) +
      (inv.IntermediaryCommission ?? 0) +
      (inv.CommissionOtherLevel ?? 0)
    );
  }

  get MonthSpread() {
    const { StartShip, EndShip } = this.MultiOrder;
    if (!StartShip || !EndShip) return 0;
    const years = EndShip.getFullYear() - StartShip.getFullYear();
    const months = EndShip.getMonth() - StartShip.getMonth();
    return years * 12 + months;
  }
  // ----- Monthly price base (FixationCurrentLevel) support -----
  private monthKey(y: number, m: number) { return `${y}-${m}`; }

  private buildMonthSpread(start: Date, end: Date) {
    const out: { ShipYear: number; ShipMonth: number; label: string }[] = [];
    const cur = new Date(start.getFullYear(), start.getMonth(), 1);
    while (cur <= end) {
      const ShipMonth = cur.getMonth() + 1;
      const ShipYear = cur.getFullYear();
      const label = `${cur.toLocaleString('en-US', { month: 'short' })} '${String(ShipYear).slice(-2)}`;
      out.push({ ShipMonth, ShipYear, label });
      cur.setMonth(cur.getMonth() + 1);
    }
    return out;
  }

  private upsertPriceBaseSpread(): void {
    if (!this.MultiOrder?.StartShip || !this.MultiOrder?.EndShip) return;
    const months = this.buildMonthSpread(this.MultiOrder.StartShip, this.MultiOrder.EndShip);
    const existing = (this.MultiOrder.PriceBaseSpread ?? []) as Array<{ ShipYear: number; ShipMonth: number; label: string; PriceBase: number | null }>;
    const byKey = new Map(existing.map(e => [this.monthKey(e.ShipYear, e.ShipMonth), e]));

    const next: typeof existing = months.map(m => {
      const key = this.monthKey(m.ShipYear, m.ShipMonth);
      const found = byKey.get(key);
      return {
        ShipYear: m.ShipYear,
        ShipMonth: m.ShipMonth,
        label: m.label,
        PriceBase: found?.PriceBase ?? null,
      };
    });

    this.MultiOrder.PriceBaseSpread = next;
  }

  private backfillPriceBaseDefaults(): void {
    if (!this.currentPriceBase || !Array.isArray(this.MultiOrder?.PriceBaseSpread)) return;
    // If you want, fill nulls with currentPriceBase
    // this.MultiOrder.PriceBaseSpread.forEach((e: any) => { if (e.PriceBase == null) e.PriceBase = this.currentPriceBase; });
  }

  private getPriceBaseForMonth(year: number, month: number): number | null {
    const list = this.MultiOrder?.PriceBaseSpread as Array<{ ShipYear: number; ShipMonth: number; PriceBase: number | null }> | undefined;
    if (!list) return null;
    return (list.find(e => e.ShipYear === year && e.ShipMonth === month)?.PriceBase) ?? null;
  }

  // ===== Futures month (FutureComposite) price-base support =====
  private closestFutureCompositeFor(ShipYear: number, ShipMonth: number) {
    const futuresMonths = this.InventoryData?.FuturesMonths || [];
    if (!futuresMonths.length) return null;

    const etd = new Date(ShipYear, ShipMonth - 1, 1);
    const etdPlus45 = new Date(etd);
    etdPlus45.setDate(etdPlus45.getDate() + 45);

    let closest = futuresMonths[0];
    let minDiff = Math.abs(new Date(closest.FND).getTime() - etdPlus45.getTime());
    for (const fm of futuresMonths) {
      const fnd = new Date(fm.FND);
      const d = Math.abs(fnd.getTime() - etdPlus45.getTime());
      if (d < minDiff) { minDiff = d; closest = fm; }
    }
    return closest;
  }

  private upsertPriceBaseByFuture(): void {
    if (!this.MultiOrder?.StartShip || !this.MultiOrder?.EndShip) return;

    const existing: Array<{ FutureCompositeID: number; FutureCompositeName: string; PriceBase: number | null }> =
      this.MultiOrder.PriceBaseByFuture ?? [];
    const byId = new Map(existing.map(e => [e.FutureCompositeID, e]));

    const start = new Date(this.MultiOrder.StartShip.getFullYear(), this.MultiOrder.StartShip.getMonth(), 1);
    const end = new Date(this.MultiOrder.EndShip.getFullYear(), this.MultiOrder.EndShip.getMonth(), 1);

    const map = new Map<number, { FutureCompositeID: number; FutureCompositeName: string; PriceBase: number | null }>();
    const cur = new Date(start);
    while (cur <= end) {
      const comp = this.closestFutureCompositeFor(cur.getFullYear(), cur.getMonth() + 1);
      if (comp) {
        const prev = byId.get(comp.FutureCompositeID);
        map.set(comp.FutureCompositeID, {
          FutureCompositeID: comp.FutureCompositeID,
          FutureCompositeName: comp.FutureCompositeName,
          PriceBase: prev?.PriceBase ?? null,
        });
      }
      cur.setMonth(cur.getMonth() + 1);
    }
    this.MultiOrder.PriceBaseByFuture = Array.from(map.values());
  }

  private backfillPriceBaseDefaultsByFuture(): void {
    if (!this.currentPriceBase || !Array.isArray(this.MultiOrder?.PriceBaseByFuture)) return;
    // this.MultiOrder.PriceBaseByFuture.forEach((e: any) => { if (e.PriceBase == null) e.PriceBase = this.currentPriceBase; });
  }

  private getPriceBaseForComposite(id: number): number | null {
    const list = this.MultiOrder?.PriceBaseByFuture as Array<{ FutureCompositeID: number; PriceBase: number | null }> | undefined;
    if (!list) return null;
    return (list.find(e => e.FutureCompositeID === id)?.PriceBase) ?? null;
  }

  private spreadKey(warehouseId: number, y: number, m: number) {
    return `${warehouseId}:${y}-${m}`;
  }
  private prefillBySpreadKey: Map<string, any[]> = new Map();

  private async hydrateFromExistingPOs(existingPOs: any[]) {
    // Fetch reference data first if not loaded
    if (!this.InventoryData?.Products) {
      this.InventoryData = await this.GetInventoryData();
    }

    // Pull shared fields (assumes 1 inventory per PO, like your builder)
    const inv0 = existingPOs[0]?.Inventory?.[0] || {};
    const po0 = existingPOs[0] || {};
    const prod = this.InventoryData.Products.find((p: any) => p.InventoryTypeID == inv0.InventoryTypeID);

    this.InventoryData.FuturesMonths =
      (this.InventoryData.AllFuturesMonths || []).filter((fm: any) => fm.Exchange === prod.FuturesExchangeKey);
    this.filteredPricings = (inv0?.FairTrade)
      ? this.InventoryData.PriceDescriptors.filter((pd: any) => pd.PriceDescriptorID != 2)
      : this.InventoryData.PriceDescriptors;

    // Seed MultiOrder from the first PO/inv
    this.MultiOrder = {
      InventoryTypeID: inv0.InventoryTypeID,
      FairTrade: inv0.FairTrade,
      FairTradeMinimum: inv0.FairTradeMinimum,
      FairTradePremium: inv0.FairTradePremium,
      OrganicPremium: inv0.OrganicPremium,
      CountryPremium: inv0.CountryPremium,
      PurchaseDate: new Date(po0.PurchaseDate ?? new Date()),
      SASTermID: inv0.SASTermID,
      TraderGUID: po0.TraderGUID,
      SupplierID: po0.SupplierID,
      SupplierAgent: po0.SupplierAgent,
      ShipmentTypeID: inv0.ShipmentTypeID,
      ShippingTerm: inv0.ShippingTerm,
      PaymentTermsID: po0.PaymentTermsID ?? inv0.PaymentTermsID,
      BuyingTermID: inv0.BuyingTermID,
      PriceDescriptorID: inv0.PriceDescriptorID,
      WeightTypeID: inv0.WeightTypeID,
      NetWeight: inv0.NetWeight,
      PriceToBeFixedByRoyal: po0.PriceToBeFixedByRoyal,
      PriceToBeFixedByShipper: po0.PriceToBeFixedByShipper,
      PriceFixedBy: inv0.PriceToBeFixedByRoyal ? 1 : 2,
      Country: inv0.Country ?? this.InventoryData.Products.find((p: any) => p.InventoryTypeID == inv0.InventoryTypeID)?.Country,
    };

    // Determine date window from existing ETDs
    const allDates = existingPOs
      .map(p => p?.Inventory?.[0])
      .filter(Boolean)
      .map((i: any) => new Date(i.ETDYear, i.ETDMonth - 1, 1));
    const min = new Date(Math.min(...allDates.map(d => +d)));
    const max = new Date(Math.max(...allDates.map(d => +d)));
    this.MultiOrder.StartShip = new Date(min.getFullYear(), min.getMonth(), 1);
    this.MultiOrder.EndShip = new Date(max.getFullYear(), max.getMonth(), 1);

    const normalized = existingPOs
      .map(p => p?.Inventory?.[0])
      .filter(Boolean)
      .sort((a: any, b: any) => (
        (a.FinalDestinationWarehouseID - b.FinalDestinationWarehouseID) ||
        (a.ETDYear - b.ETDYear) ||
        (a.ETDMonth - b.ETDMonth) ||
        ((a.InventoryID ?? 0) - (b.InventoryID ?? 0))
      ));

    const prefill = new Map<string, any[]>();
    for (const i of normalized) {
      const key = this.spreadKey(i.FinalDestinationWarehouseID, i.ETDYear, i.ETDMonth);
      const arr = prefill.get(key) ?? [];
      arr.push({
        SupplierReffNumber: i.SupplierReffNumber ?? null,
        Notes: i.Notes ?? null,
        WebInvisible: !!i.WebInvisible,
        ExWhseCost: i.ExWhseCost ?? null,
        FOBtoExWarehouseValueTemp: i.FOBtoExWarehouseValueTemp ?? null,
        InventoryID: i.InventoryID ??null
      });
      prefill.set(key, arr);
    }
    this.prefillBySpreadKey = prefill;

    // Build price base maps
    const invs = existingPOs.map(p => p?.Inventory?.[0]).filter(Boolean);

    const compositeFix = new Map<number, number>();
    const compositeNames = new Map<number, string>();

    for (const i of invs) {
      const comp = i.FutureCompositeID ?? this.closestFutureCompositeFor(i.ETDYear, i.ETDMonth)?.FutureCompositeID;
      const fix = (i.FixationCurrentLevel != null) ? Number(i.FixationCurrentLevel) : null;

      if (comp != null) {
        if (fix != null && !compositeFix.has(comp)) compositeFix.set(comp, fix);
        const meta = (this.InventoryData.FuturesMonths || [])
          .find((fm: any) => fm.FutureCompositeID === comp);
        if (meta?.FutureCompositeName) compositeNames.set(comp, meta.FutureCompositeName);
      }
    }

    const monthFix = new Map<string, number>();
    for (const i of invs) {
      if (i.FixationCurrentLevel == null) continue;
      const key = this.monthKey(Number(i.ETDYear), Number(i.ETDMonth));
      monthFix.set(key, Number(i.FixationCurrentLevel));
    }

    this.upsertPriceBaseByFuture();

    if (Array.isArray(this.MultiOrder.PriceBaseByFuture)) {
      this.MultiOrder.PriceBaseByFuture.forEach((e: any) => {
        const val = compositeFix.get(e.FutureCompositeID);
        if (val != null) e.PriceBase = val;
        if (!e.FutureCompositeName && compositeNames.has(e.FutureCompositeID)) {
          e.FutureCompositeName = compositeNames.get(e.FutureCompositeID);
        }
      });
    }

    this.upsertPriceBaseSpread();

    if (Array.isArray(this.MultiOrder.PriceBaseSpread)) {
      this.MultiOrder.PriceBaseSpread.forEach((m: any) => {
        const key = this.monthKey(m.ShipYear, m.ShipMonth);
        let val = monthFix.get(key);
        if (val == null) {
          const comp = this.closestFutureCompositeFor(m.ShipYear, m.ShipMonth);
          if (comp) val = compositeFix.get(comp.FutureCompositeID) ?? undefined;
        }
        if (val != null) m.PriceBase = val;
      });
    }

    if (!this.currentPriceBase) {
      const firstPB =
        (this.MultiOrder.PriceBaseByFuture?.find((x: any) => x.PriceBase != null)?.PriceBase) ??
        (this.MultiOrder.PriceBaseSpread?.find((x: any) => x.PriceBase != null)?.PriceBase) ??
        null;
      if (firstPB != null) this.currentPriceBase = Number(firstPB);
    }

    this.generateMonthYearOptions();
    this.filterEndMonthOptions();
    this.upsertPriceBaseByFuture();

    // Group existing by warehouse
    const byWarehouse = new Map<number, any>();
    for (const po of existingPOs) {
      const inv = po?.Inventory?.[0]; if (!inv) continue;
      const wid = inv.FinalDestinationWarehouseID;

      if (!byWarehouse.has(wid)) {
        const wh = this.InventoryData.DestinationWarehouses.find((dw: any) => dw.LocationID == wid);
        byWarehouse.set(wid, {
          id: crypto.randomUUID?.() ?? Math.random().toString(),
          FinalDestinationWarehouseID: wid,
          Destination: wh?.DestinationId ?? null,
          Spread: [],
          PricePerUnit: this.MultiOrder.BuyingTermID === 1 ? inv.PricePerUnit ?? null : null,
          DifferentialPerUnit: this.MultiOrder.BuyingTermID === 2 ? inv.DifferentialPerUnit ?? null : null,
          PerPoundCost: '$0.0000',
          PerPoundCostValue: this.MultiOrder.BuyingTermID === 1 ? (inv.PricePound ?? 0) : (inv.DifferentialPerPound ?? 0),
        });
      }
    }

    // Build spread rows across the date range
    const cur = new Date(this.MultiOrder.StartShip);
    while (cur <= this.MultiOrder.EndShip) {
      const ShipMonth = cur.getMonth() + 1;
      const ShipYear = cur.getFullYear();
      const label = `${cur.toLocaleString('en-US', { month: 'short' })} '${String(ShipYear).slice(-2)}`;

      for (const [wid, w] of byWarehouse.entries()) {
        const count = existingPOs
          .map(p => p?.Inventory?.[0])
          .filter(Boolean)
          .filter((i: any) => i.FinalDestinationWarehouseID == wid && i.ETDMonth == ShipMonth && i.ETDYear == ShipYear)
          .length;

        w.Spread.push({ label, ShipMonth, ShipYear, Containers: count || null });
      }
      cur.setMonth(cur.getMonth() + 1);
    }
    this.seedDecafFromExisting(existingPOs, byWarehouse);
    this.Warehouses = Array.from(byWarehouse.values());
    this.TableExpand = this.Warehouses.some((w: any) => !!w.Decaf);
    this.TotalContainers = existingPOs.length;
    this.BagsPerContainer = inv0.BagsOrdered ?? null;

    this.EditForm = true;
    this.onAnyFieldChanged();
  }
  /** Look at existing POs; if a child (DecaffeinatorID) exists, enable decaf on that warehouse and seed the child panel */
  private seedDecafFromExisting(existingPOs: any[], byWarehouse: Map<number, any>) {
    // Build index of children by (warehouseId, ShipYear, ShipMonth) → child
    const childByKey = new Map<string, any>();

    for (const po of existingPOs ?? []) {
      const items: any[] = Array.isArray(po?.Inventory) ? po.Inventory : [];
      const parent = items.find(i => !i?.ParentInventoryID) ?? items[0];
      const child = items.find(i => !!i?.DecaffeinatorID);

      if (!parent || !child) continue;

      const key = this.spreadKey(parent.FinalDestinationWarehouseID, parent.ETDYear, parent.ETDMonth);
      childByKey.set(key, child);

      // Also mark the warehouse as decaf-capable overall
      const w = byWarehouse.get(parent.FinalDestinationWarehouseID);
      if (!w) continue;

      // Ensure decaf mode + defaults for that warehouse
      this.onChangeWarehouse(w, w.FinalDestinationWarehouseID); // seeds w.Decaf=true and default w.Child
      w.Decaf = true;

      // Copy important child fields onto the child panel so the UI shows exactly what was saved
      w.Child = {
        ...(w.Child ?? {}),
        DecaffeinatorID: child.DecaffeinatorID,
        InventoryTypeID: child.InventoryTypeID ?? (w.Child?.InventoryTypeID ?? null),
        FinalDestinationWarehouseID: child.FinalDestinationWarehouseID ?? w.FinalDestinationWarehouseID,
        Destination: child.Destination ?? w.Destination ?? null,
        BagsOrdered: child.BagsOrdered ?? w.Child?.BagsOrdered ?? null,
        NetWeight: child.NetWeight ?? w.Child?.NetWeight ?? null,
        InventoryID: child.InventoryID ?? null,
        // keep your decaf costing knobs if they were persisted
        DecafCostTotal: child.DecafCostTotal ?? w.Child?.DecafCostTotal ?? 0,
        decafSeaforth: child.decafSeaforth ?? w.Child?.decafSeaforth ?? (child.DecaffeinatorID == 6815 ? 800 : 0),
        decafStrippingPerBag: child.decafStrippingPerBag ?? w.Child?.decafStrippingPerBag ?? 1.10,
        decafStoragePerBag: child.decafStoragePerBag ?? w.Child?.decafStoragePerBag ?? 0.79,
        decafWeighingPerBag: child.decafWeighingPerBag ?? w.Child?.decafWeighingPerBag ?? 0.63,
        decafLossValuePercent: child.decafLossValuePercent ?? w.Child?.decafLossValuePercent ?? 6.25,
        decafFinancingPercent: child.decafFinancingPercent ?? w.Child?.decafFinancingPercent ?? 10.00,
        decafBuffer: child.decafBuffer ?? w.Child?.decafBuffer ?? 0.03,
        decafTrucking: child.decafTrucking ?? w.Child?.decafTrucking ?? 0,
        tollingCost: child.tollingCost ?? w.Child?.tollingCost ?? 0,
      };
    }

    // Optional: if you want to reflect per-month decaf presence in the spread UI
    for (const w of byWarehouse.values()) {
      for (const s of (w.Spread ?? [])) {
        const key = this.spreadKey(w.FinalDestinationWarehouseID, s.ShipYear, s.ShipMonth);
        s.HasDecafChild = childByKey.has(key);
      }
    }
  }

  // ===========================================================================
  // Warehouse Validation
  // ===========================================================================

  // ==== Warehouse validation helpers (kept) ====
  WarehouseFormError(): string | null {
    const totalSelected = this.getTotalContainers();
    const totalAllowed = Number(this.TotalContainers ?? 0);
    const emptyWarehouseExists = this.emptyWarehouse();

    if (emptyWarehouseExists) {
      return 'One or more warehouses have no containers assigned in any month. Either remove it or add containers.';
    }
    if (totalSelected > totalAllowed) {
      return 'You have too many containers. Either remove some from your spread or add to your total at top of this form.';
    }
    if (totalSelected < totalAllowed) {
      return 'You do not have enough containers in your spread. Continue adding containers or update your total at top of this form.';
    }
    return null;
  }

  private emptyWarehouse(): boolean {
    // Ignore the first warehouse, require every additional one to have at least one container.
    for (let i = 1; i < this.Warehouses.length; i++) {
      const w = this.Warehouses[i];
      const hasContainer = Array.isArray(w?.Spread)
        && w.Spread.some((s: any) => s?.Containers != null && Number(s.Containers) > 0);
      if (!hasContainer) return true;
    }
    return false;
  }

  private getTotalContainers(): number {
    let total = 0;
    for (const w of this.Warehouses) {
      if (!Array.isArray(w?.Spread)) continue;
      for (const s of w.Spread) {
        const n = Number(s?.Containers);
        if (!Number.isNaN(n)) total += n;
      }
    }
    return total;
  }

  RemainingContainers(): string {
    const remaining = Number(this.TotalContainers ?? 0) - this.getTotalContainers();
    if (remaining >= 0) return `${remaining} Containers Remaining.`;
    const over = Math.abs(remaining);
    return `Over by ${over} ${over === 1 ? 'Container.' : 'Containers.'}`;
  }

  // ===========================================================================
  // Decaf helpers (NEW)
  // ===========================================================================

  /** Map of destination warehouse → Decaffeinator VendorID (adjust IDs as needed) */

  /**
   * If you want the preview table to show decaf as children under parents
   * (in case you switched your HTML to the expandable layout), call this after you rebuild `this.Inventory`.
   */
  private buildPreviewRows() {
    const parentsMap = new Map<string | number, any>();
    const childrenFlat: any[] = [];

    for (const inv of this.Inventory ?? []) {
      if (inv?.ParentInventoryID) {
        childrenFlat.push(inv);
      } else {
        const key = inv.InventoryID ?? inv._tempKey ?? Math.random().toString(36).slice(2);
        const existingKids = Array.isArray(inv.ChildInventory) ? inv.ChildInventory.map((c: any) => ({ ...c })) : [];
        // also lift a plain `child` (your onBuildPurchaseOrders uses `inv.child`)
        if (inv.child) existingKids.push({ ...inv.child });
        parentsMap.set(key, { ...inv, ChildInventory: existingKids });
      }
    }

    for (const child of childrenFlat) {
      const parent = parentsMap.get(child.ParentInventoryID);
      if (parent) parent.ChildInventory.push(child);
      else {
        const key = child.InventoryID ?? Math.random().toString(36).slice(2);
        parentsMap.set(key, { ...child, ChildInventory: [] });
      }
    }
    
    const toDisplay = (inv: any) => ({
      ...inv,
      PriceHeader: inv.BuyingTermID == 1 ? 'Cost' : 'Differential',
      PriceLabel: inv.CostPerUnitLabel,
      ShippingHeader: inv.ShipmentTypeID == 1 && inv.ShippingTerm != 4 ? 'FOBtoExWarehouse ($\\lb.)' : 'ExWarehouse ($\\lb.)',
      LandedHeader: inv.BuyingTermID == 1 ? 'Estimated Landed Cost ($\\lb.)' : 'Estimated Landed Diff ($\\lb.)',
      ShippingLabel: inv.ShipmentTypeID == 1 && inv.ShippingTerm != 4 ? inv.FOBtoExWarehouseValueTemp : inv.ExWhseCost,
      LandedLabel: inv.BuyingTermID == 1 ? inv.FinalWarehouseInCost : inv.FinalWarehouseInDifferential,
    });
    
    this.InventoryForTable = Array.from(parentsMap.values()).map((p: any) => ({
      ...toDisplay(p),
      ChildInventory: Array.isArray(p.ChildInventory) ? p.ChildInventory.map((c: any) => toDisplay(c)) : [],
    }));
    this.autoExpand = this.InventoryForTable.some(p => (p.ChildInventory?.length ?? 0) > 0);
    console.log(this.InventoryForTable);
  }

  
  /**
   * Flatten parent + (child / ChildInventory[]) for API saves.
   * Works with both shapes:
   *  - parent.child (your current onBuildPurchaseOrders)
   *  - parent.ChildInventory: [child, ...] 
   */
  private flattenForSave(items: any[]): any[] {
    const out: any[] = [];
    for (const p of items ?? []) {
      // Support both shapes during save
      const childFromProp = p?.child ? [p.child] : [];
      const kids = Array.isArray(p?.ChildInventory) ? p.ChildInventory : childFromProp;

      const { ChildInventory, child, _tempKey, ...parentClean } = p;
      out.push(parentClean);

      for (const kid of (kids ?? [])) {
        const { _parentTempKey, readOnly, IsChild, Child, ...childClean } = kid ?? {};
        childClean.ParentInventoryID = parentClean.InventoryID ?? childClean.ParentInventoryID ?? null;
        out.push(childClean);
      }
    }
    return out;
  }

  /** After create/reload, make sure child rows point at the new parent InventoryID, then persist again */
  private ensureDecafParentLink(po: any): boolean {
    const items: any[] = Array.isArray(po?.Inventory) ? po.Inventory : [];
    if (items.length < 2) return false;

    const bases = items.filter(i => !i?.DecaffeinatorID);
    const kids = items.filter(i => i?.DecaffeinatorID);
    let changed = false;

    for (const kid of kids) {
      if (kid.ParentInventoryID) continue;
      const parent = bases[0];
      if (parent?.InventoryID && kid.ParentInventoryID !== parent.InventoryID) {
        kid.ParentInventoryID = parent.InventoryID;
        changed = true;
      }
    }
    return changed;
  }

  // ===========================================================================
  // Save (REPLACED): now supports decaf child save flow
  // ===========================================================================

  async savePurchaseOrders() {
    if (this.saving || this.saveOutcomeUnknown || !this.PurchaseOrders.length) return;
    this.saving = true;
    try {
    // Build buckets of existing POs *if editing an existing spread*

    const existing = (this.selectedPurchaseOrders ?? []).slice();
    const buckets = new Map<string, any[]>();
    for (const po of existing) {
      const inv = po?.Inventory?.[0]; if (!inv) continue;
      const key = (po.SpreadKey ?? this.spreadKey(inv.FinalDestinationWarehouseID, inv.ETDYear, inv.ETDMonth));
      const arr = buckets.get(key) ?? [];
      arr.push(po);
      buckets.set(key, arr);
    }

    // Attach IDs from an existing one (update) or leave blank (create)
    for (const po of this.PurchaseOrders) {
      const parentNew = po?.Inventory?.[0];
      if (!parentNew) continue;

      const key = po.SpreadKey ?? this.spreadKey(parentNew.FinalDestinationWarehouseID, parentNew.ETDYear, parentNew.ETDMonth);
      const pool = buckets.get(key);

      if (pool?.length) {
        // FIFO match for this (warehouse, year, month)
        const match = pool.shift()!;

        po.PurchaseOrderID = match.PurchaseOrderID ?? null;

        // ----- parent ID -----
        const parentOld = (match.Inventory ?? []).find((i: any) => !i?.ParentInventoryID) ?? match.Inventory?.[0];
        if (parentOld?.InventoryID) {
          parentNew.InventoryID = parentOld.InventoryID;
        }

        // ----- child ID (decaf) -----
        const childNew = (po.Inventory ?? []).find((i: any) => !!i?.DecaffeinatorID);
        const childOld = (match.Inventory ?? []).find((i: any) => !!i?.DecaffeinatorID);

        if (childNew && childOld) {
          childNew.InventoryID = childOld.InventoryID ?? null;
          // make sure link points at this PO's parent
          childNew.ParentInventoryID =
            parentNew.InventoryID ??
            parentOld?.InventoryID ??
            childOld.ParentInventoryID ?? null;
        }
      }
    }

    // Any leftover in buckets are **deletions**
    const toDelete: number[] = [];
    for (const [, arr] of buckets.entries()) {
      for (const p of arr) {
        if (p?.PurchaseOrderID) toDelete.push(p.PurchaseOrderID);
      }
    }

    if (toDelete.length) {
      this.message.error('This change would remove existing POs: ' + toDelete.join(', ') + '. Keep those allocations until the deletion workflow is available. Nothing was saved.');
      return;
    }
    const result = await lastValueFrom(this.web.getWebCall('inventory', 'coffee/purchase-order/save-spread', {
      body: { orders: this.PurchaseOrders, existingIds: existing.map(po => po.PurchaseOrderID).filter(Boolean) }
    }));
    this.onSavePOSpread.emit(result.PurchaseOrderSpread);
    } catch (error: any) {
      this.saveOutcomeUnknown = error?.status === 0;
      this.message.error(this.saveOutcomeUnknown
        ? 'The connection was interrupted. Check the purchase order list before saving again; the server may have completed the save.'
        : (error?.error?.error ?? error?.message ?? 'Unable to save the spread. Your changes are still available.'));
    } finally { this.saving = false; }
  }
  private normalizeNullables<T extends object>(row: T, keys: string[]): T {
    const out: any = { ...row };
    for (const k of keys) {
      if (!(k in out) || out[k] === undefined) out[k] = null;
    }
    return out;
  }

  // ===========================================================================
  // Navigation / UI
  // ===========================================================================
  onReturnToList() {
    if (this.saving || this.building) return;
    if (confirm('Spread Purchase Order not saved. Are you sure you want to leave?')) {
      this.CancelForm.emit();
    }
  }
  // === Calc modal state ===
  calcModalData: any | null = null;

  // Build payload for <app-calc-form> from a row in the table
  private buildCalcDataFromRow(row: any) {
    const decaf = !!row?.DecaffeinatorID;

    const isChild = !!row?.DecaffeinatorID;

    const po = this.PurchaseOrders?.find((p: any) => p.poNum === row.poNum);
    const items = Array.isArray(po?.Inventory) ? po.Inventory : [];
    const parent = isChild ? (items.find((i: any) => !i?.ParentInventoryID) ?? row._parent) : row;

    const parentBase = Number(parent?.FinalWarehouseInCost ?? parent?.EstimatedCost ?? 0);
    const parentNet = Number(parent?.netWeight ?? row?.netWeight ?? 0);
    const commissions = Number(parent?.TotalCommissions ?? row?.TotalCommissions ?? 0);

    const base = {
      Decaf: isChild,
      WorstCase: !!row.WorstCase,

      // handling mode
      ShippingTerm: row.ShippingTerm,
      ShipmentTypeID: row.ShipmentTypeID,
      FOBtoExWarehouseValueTemp: row.FOBtoExWarehouseValueTemp ?? null,
      ExWhseCost: row.ExWhseCost ?? null,

      // weights & price
      netWeight: row.netWeight,
      grossWeight: row.grossWeight,
      totalBags: row.BagsOrdered,
      cost: row.cost,

      // port/fees (editable ones will be enabled inside calc-form)
      steamShipRate: row.steamShipRate,
      marineInsurance: row.marineInsurance,
      dutyAndEntry: row.dutyAndEntry,
      drayage: row.drayage,
      stripping: row.stripping,
      storage: row.storage,
      weighing: row.weighing,
      financing: row.financing,
      fot: row.fot,
      sampleCharge: row.sampleCharge,
      rfa: row.rfa,
      rateBuffer: row.rateBuffer,
      finalCostWith: row.finalCostWith,
      DaysToFinance: row.DaysToFinance,

      // derived
      totalCost: row.totalCost,
      steamShipRatePerPound: row.steamShipRatePerPound,
      marineInsurancePerPound: row.marineInsurancePerPound,
      dutyAndEntryPerPound: row.dutyAndEntryPerPound,
      drayagePerPound: row.drayagePerPound,
      strippingPerPound: row.strippingPerPound,
      storagePerPound: row.storagePerPound,
      weighingPerPound: row.weighingPerPound,
      financingPerPound: row.financingPerPound,
      fotPerPound: row.fotPerPound,
      sampleChargePerPound: row.sampleChargePerPound,
      rfaPerPound: row.rfaPerPound,
      totalEstimate: row.totalEstimate,
      parentBaseFinalPerLb: parentBase,
      parentNetWeight: parentNet,
      totalCommissions: commissions,
    };

    if (!decaf) return base;

    return {
      ...base,
      DecaffeinatorID: row.DecaffeinatorID,
      DecafCostPerPound: row.DecafCostPerPound ?? 0.76,
      DecafCostTotal: row.DecafCostTotal ?? 0,
      decafSeaforth: row.DecaffeinatorID === 6815 ? 800 : (row.decafSeaforth ?? 0),

      decafStripping: row.decafStripping ?? 0,
      decafStrippingPerBag: row.decafStrippingPerBag ?? 1.10,
      decafStorage: row.decafStorage ?? 0,
      decafStoragePerBag: row.decafStoragePerBag ?? 0.79,
      decafWeighing: row.decafWeighing ?? 0,
      decafWeighingPerBag: row.decafWeighingPerBag ?? 0.63,

      decafLossValue: row.decafLossValue ?? 0,
      decafLossValuePercent: row.decafLossValuePercent ?? 6.25,
      totalDecafLossValue: row.totalDecafLossValue ?? 0,
      decafFinancing: row.decafFinancing ?? 0,
      decafFinancingPercent: row.decafFinancingPercent ?? 10.0,

      totalDecafNetWeight: row.totalDecafNetWeight ?? 0,
      totalDecafNetWeightFromLoss: row.totalDecafNetWeightFromLoss ?? 0,
      decafBuffer: row.decafBuffer ?? 0.03,
      decafTolling: row.decafTolling ?? 0,
      decafTollingPerPound: row.decafTollingPerPound ?? 0,
      totalOrderWithDecafCost: row.totalOrderWithDecafCost ?? 0,
      decafTrucking: row.decafTrucking ?? 0,
      tollingCost: row.tollingCost ?? 0,
      childBagsOrdered: Number(row.BagsOrdered ?? 0),
      parentPricePound: parent?.PricePound ?? null,
      parentDifferentialPerPound: parent?.DifferentialPerPound ?? null,
      parentFixationCurrentLevel: parent?.FixationCurrentLevel ?? null,
    };
  }

  // Open modal from the info icon
  openCostingWorksheet(row: any) {
    const { inv } = this.resolveInventoryRefFromRow(row, /* preferDecafChild */ true);
    this.SelectedInventory = inv;                 // <- may be the child now
    this.calcModalData = this.buildCalcDataFromRow(inv); // <- Decaf:true if child
  }


  // Close modal
  onCalcClose() {
    this.closingTotals = true;
    setTimeout(() => {
      this.SelectedInventory = null;
      this.calcModalData = null;
      this.closingTotals = false;
    }, 300);
  }

  // Apply edits from <app-calc-form> back onto the selected row, then recalc
  async handleTotalsChange(data: any) {
    if (!this.SelectedInventory) return;

    const isDecaf = !!data.Decaf;

    if (!isDecaf) {
      this.SelectedInventory.CostOverrides = { ...data };
      // editable fields (non-decaf)
      this.SelectedInventory.steamShipRate = data.steamShipRate ?? this.SelectedInventory.steamShipRate;
      this.SelectedInventory.marineInsurance = data.marineInsurance ?? this.SelectedInventory.marineInsurance;
      this.SelectedInventory.fot = data.fot ?? this.SelectedInventory.fot;
      this.SelectedInventory.rfa = data.rfa ?? this.SelectedInventory.rfa;
      this.SelectedInventory.finalCostWith = data.finalCostWith ?? this.SelectedInventory.finalCostWith;
      this.SelectedInventory.rateBuffer = data.rateBuffer ?? this.SelectedInventory.rateBuffer;
      this.SelectedInventory.DaysToFinance = data.DaysToFinance ?? this.SelectedInventory.DaysToFinance;

      // re-run line calc with its spread context
      const ctx = this.findSpreadContext(this.SelectedInventory);
      if (ctx) await this.recalculateInventoryLine(this.SelectedInventory, ctx.s, ctx.w);
    } else {
      // editable fields (decaf)
      this.SelectedInventory.DecafCostPerPound = data.DecafCostPerPound ?? this.SelectedInventory.DecafCostPerPound;
      this.SelectedInventory.decafStrippingPerBag = data.decafStrippingPerBag ?? this.SelectedInventory.decafStrippingPerBag;
      this.SelectedInventory.decafStoragePerBag = data.decafStoragePerBag ?? this.SelectedInventory.decafStoragePerBag;
      this.SelectedInventory.decafWeighingPerBag = data.decafWeighingPerBag ?? this.SelectedInventory.decafWeighingPerBag;
      this.SelectedInventory.decafBuffer = data.decafBuffer ?? this.SelectedInventory.decafBuffer;
      this.SelectedInventory.decafTrucking = data.decafTrucking ?? this.SelectedInventory.decafTrucking;

      // need the parent + spread context to recalc decaf
      const po = this.PurchaseOrders.find((p: any) => p.poNum === this.SelectedInventory.poNum);
      const parent = po?.Inventory?.find((i: any) => !i?.ParentInventoryID) ?? null;
      const ctx = parent ? this.findSpreadContext(parent) : null;
      if (parent && ctx) {
        const recalcChild = await this.recalculateDecafInventoryLine(this.SelectedInventory, parent, ctx.w, ctx.s);
        Object.assign(this.SelectedInventory, recalcChild);
      }
    }

    // refresh UI
    this.Inventory = [...this.Inventory];
    this.buildPreviewRows();
    this.calcModalData = this.buildCalcDataFromRow(this.SelectedInventory); // keep modal numbers in sync as user types
  }
  private resolveInventoryRefFromRow(row: any, preferDecafChild = true): { po: any | null, inv: any, parent?: any, isChild: boolean } {
    const po = this.PurchaseOrders.find((p: any) => p.poNum === row.poNum) ?? null;
    if (!po) return { po: null, inv: row, isChild: !!row?.DecaffeinatorID };

    const items = Array.isArray(po.Inventory) ? po.Inventory : [];
    const parent = items.find((i: any) => !i?.ParentInventoryID) ?? items[0];
    const child = items.find((i: any) => !!i?.DecaffeinatorID) ?? null;

    // If the table row has a child, or we prefer children and one exists, use it
    let inv = row?.DecaffeinatorID ? (child ?? row) :
      preferDecafChild && child ? child : parent;

    // Ensure mapping back to this PO works even if child didn’t have poNum originally
    if (inv && inv.poNum == null) inv.poNum = po.poNum;

    return { po, inv, parent, isChild: !!inv?.DecaffeinatorID };
  }
  getUnitsForPurchasePrice(): string {
    return this.MultiOrder.PriceDescriptorID ? (this.InventoryData.PriceDescriptors.find((pd: any) => pd.PriceDescriptorID === this.MultiOrder.PriceDescriptorID).PriceDescriptorName) : '';
  }
}
